import { store, getContext, getElement } from '@wordpress/interactivity';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet-gesture-handling/dist/leaflet-gesture-handling.css';
import 'leaflet-gesture-handling';

function createModernSVGMarker(color, size = 20) {
    return `
        <svg xmlns="http://www.w3.org/2000/svg" width="${size * 2}" height="${size * 2}" viewBox="0 0 ${size * 2} ${size * 2}">
            <defs>
                <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
                    <feDropShadow dx="0" dy="2" stdDeviation="3" flood-color="#000" flood-opacity="0.2"/>
                </filter>
            </defs>
            <circle cx="${size}" cy="${size}" r="${size * 0.5}" fill="${color}" stroke="#FFF" stroke-width="2" filter="url(#shadow)"/>
        </svg>
    `;
}

function resetActiveElements(queryElement) {
    const postTemplate = queryElement.querySelector('.wp-block-post-template');
    if (postTemplate) postTemplate.classList.remove('active');
    queryElement.querySelectorAll('[data-gt-camping]').forEach(el => el.classList.remove('active'));
}

function activateCamping(queryElement, campingId) {
    const el = queryElement.querySelector(`[data-gt-camping="${campingId}"]`);
    if (el) el.classList.add('active');
    const details = queryElement.querySelector('.wp-block-post-template');
    if (details) details.classList.add('active');
}

function adjustMapHeight(map, queryElement) {
    const detailsElement = queryElement.querySelector('.wp-block-post-template');
    if (detailsElement) {
        const detailsHeight = detailsElement.scrollHeight;
        map.getContainer().style.height = `${detailsHeight}px`;
    }
    map.invalidateSize();
}

function resetMarkers(state, accentColor, markerPositions, map) {
    Object.values(state.markers).forEach(marker => {
        marker.setIcon(L.divIcon({
            html: createModernSVGMarker(accentColor),
            className: 'modern-svg-marker',
            iconSize: [40, 40],
            iconAnchor: [20, 20],
        }));
    });
    if (markerPositions.length > 0) {
        const bounds = L.latLngBounds(markerPositions);
        state.map.fitBounds(bounds, { padding: [100, 100], maxZoom: 10 });
    }
    setTimeout(() => map.invalidateSize(), 200);
}

function selectCampingAndUpdateUI(state, camping, element, map) {
    if (!camping) return;
    state.selectedCamping = camping;

    const queryElement = element.ref.closest('.wp-block-query');
    resetActiveElements(queryElement);
    activateCamping(queryElement, camping.idSite);

    const marker = state.markers[camping.idSite];
    if (marker) {
        map.setView(marker.getLatLng(), 9);
        marker.openPopup();
    }

    setTimeout(() => adjustMapHeight(map, queryElement), 200);
}

const { state } = store('gt/ctv', {
    state: {
        map: null,
        markers: {},
        initMap: false
    },
    actions: {
        initMap: () => {
            if (state.initMap) return;
            state.initMap = true;
            

            const context = getContext();
            const element = getElement();
            const accentColor = context.accentColor || '#3498db';

            const map = L.map(context.mapId, {
                scrollWheelZoom: false,
                gestureHandling: true,
            }).setView([46.603354, 1.888334], 9);

            state.map = map;

            L.tileLayer('https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png', {
                attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/">CARTO</a>',
                subdomains: 'abcd',
                maxZoom: 20,
            }).addTo(map);

            const markerPositions = [];
            state.campings.forEach(camping => {
                const { latitude, longitude } = camping.gpsCoordinates;

                const customIcon = L.divIcon({
                    html: createModernSVGMarker(accentColor),
                    className: 'modern-svg-marker',
                    iconSize: [40, 40],
                    iconAnchor: [20, 20],
                });

                const marker = L.marker([latitude, longitude], { icon: customIcon }).addTo(map);
                marker.bindPopup(`<b>${camping.name}</b>`);

                marker.on('mouseover', () => {
                    state.selectedCamping = camping;
                    const queryElement = element.ref.parentNode;
                    resetActiveElements(queryElement);
                    activateCamping(queryElement, camping.idSite);

                    marker.openPopup();

                    setTimeout(() => adjustMapHeight(map, queryElement), 200);
                });

                marker.on('popupclose', () => {
                    if (state.selectedCamping?.idSite === camping.idSite) {
                        const queryElement = element.ref.parentNode;
                        resetActiveElements(queryElement);
                        state.selectedCamping = false;
                        resetMarkers(state, accentColor, markerPositions, map);
                    }
                });

                markerPositions.push([latitude, longitude]);
                state.markers[camping.idSite] = marker;
            });

            if (markerPositions.length > 0) {
                const bounds = L.latLngBounds(markerPositions);
                state.map.fitBounds(bounds, { padding: [100, 100], maxZoom: 10 });
            }
            

        },
        updateMap: () => {

            if(!state.map)return;

            const markerPositions = Object.values(state.markers).map(marker => marker.getLatLng());
            if (markerPositions.length > 0) {
                const bounds = L.latLngBounds(markerPositions);
                state.map.fitBounds(bounds, { padding: [100, 100], maxZoom: 10 });
            }

            setTimeout(() => state.map?.invalidateSize(), 200);

            if (!state.visibleCampingsIds) return;

            Object.keys(state.markers).forEach(idSite => {
                const marker = state.markers[idSite];
                if (state.visibleCampingsIds.includes(idSite)) {
                    marker.addTo(state.map);
                } else {
                    state.map.removeLayer(marker);
                }
            });

            const activeMarkers = state.visibleCampingsIds.map(id => state.markers[id].getLatLng());
            if (activeMarkers.length > 0) {
                const bounds = L.latLngBounds(activeMarkers);
                state.map.fitBounds(bounds, { padding: [100, 100], maxZoom: 10 });
            }

            setTimeout(() => state.map?.invalidateSize(), 200);
        },
        selectNextCamping: () => {
            const element = getElement();
            const campingsToUse = state.visibleCampingsIds
                ? state.campings.filter(c => state.visibleCampingsIds.includes(c.idSite.toString()))
                : state.campings;

            if (campingsToUse.length === 0) return;

            const currentIndex = campingsToUse.findIndex(c => c.idSite === state.selectedCamping?.idSite);
            const nextIndex = (currentIndex + 1) % campingsToUse.length;
            selectCampingAndUpdateUI(state, campingsToUse[nextIndex], element, state.map);
        },
        selectPreviousCamping: () => {
            const element = getElement();
            const campingsToUse = state.visibleCampingsIds
                ? state.campings.filter(c => state.visibleCampingsIds.includes(c.idSite.toString()))
                : state.campings;

            if (campingsToUse.length === 0) return;

            const currentIndex = campingsToUse.findIndex(c => c.idSite === state.selectedCamping?.idSite);
            const previousIndex = (currentIndex - 1 + campingsToUse.length) % campingsToUse.length;
            selectCampingAndUpdateUI(state, campingsToUse[previousIndex], element, state.map);
        },
        closeCampingDetails: () => {
            const element = getElement();
            const queryElement = element.ref.closest('.wp-block-query');
            resetActiveElements(queryElement);
            state.selectedCamping = false;

            const markerPositions = Object.values(state.markers).map(marker => marker.getLatLng());
            if (markerPositions.length > 0) {
                const bounds = L.latLngBounds(markerPositions);
                state.map.fitBounds(bounds, { padding: [100, 100], maxZoom: 10 });
            }
            setTimeout(() => state.map.invalidateSize(), 200);
        },
        toggleMap: () => { 
            state.showMap = true;
            state.showList = false;
            state.selectedCamping = false;
    
            // Réinitialiser les classes actives
            const postTemplate = document.querySelector('.wp-block-post-template');
            if (postTemplate) postTemplate.classList.remove('active');
            document.querySelectorAll('[data-gt-camping]').forEach(el => el.classList.remove('active'));
    
            // Récupérer les positions des marqueurs visibles
            const markerPositions = Object.values(state.markers).map(marker => marker.getLatLng());
            if (markerPositions.length > 0) {
                const bounds = L.latLngBounds(markerPositions);
                state.map.fitBounds(bounds, { padding: [100, 100], maxZoom: 10 });
            }
    
            // Réinitialiser la taille de la carte après un délai
            setTimeout(() => state.map?.invalidateSize(), 200);

            state.isLoading = true;
            state.loaded = false;
            store('gt/ctv').actions.submitForm();
        },
        toggleList: () => {
            state.showList = true;
            state.showMap = false;
            state.selectedCamping = false;
            state.isLoading = true;
            state.loaded = false;

            const postTemplate = document.querySelector('.wp-block-post-template');
            if (postTemplate) postTemplate.classList.remove('active');
            document.querySelectorAll('[data-gt-camping]').forEach(el => el.classList.remove('active'));

            store('gt/ctv').actions.submitForm();
        }
    }
});

async function updateUrl() {
    const router = await import('@wordpress/interactivity-router');

    const url = new URL(window.location);
    url.searchParams.set('arrival', state.gtArrival);
    url.searchParams.set('departure', state.gtDeparture);
    url.searchParams.set('nbPers', state.gtNbPers);

    await router.actions.navigate(url.toString(), {});
}
